import { useState, useCallback } from 'react';
import { Car, Users, UserCheck, Bus, Luggage, Check, X, Plane } from 'lucide-react';
import { classifyFlightLegs } from '../utils/connectionAwareness';
import { validatePreference } from '../utils/preferenceValidation';
import { saveJourneyPreferences } from '../services/journeyPreferencesService';
import { colors, spacing, typography, fontWeights, cardStyles, pillButtonStyle } from '../styles/designTokens';

// ── Option definitions ────────────────────────────────────────────────────────

const TRANSPORT_OPTIONS = [
  { label: 'Driving', value: 'driving', Icon: Car },
  { label: 'Rideshare', value: 'rideshare', Icon: Users },
  { label: 'Drop-off', value: 'dropoff', Icon: UserCheck },
  { label: 'Public Transit', value: 'public_transit', Icon: Bus },
];

const CHECKED_BAGS_OPTIONS = [
  { label: 'Yes', value: true, Icon: Luggage },
  { label: 'No', value: false, Icon: X },
];

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  container: {
    ...cardStyles.container,
    margin: spacing.cardMargin,
  },
  header: {
    ...cardStyles.header,
  },
  headerText: {
    ...cardStyles.headerText,
  },
  body: {
    padding: spacing.cardPadding,
    backgroundColor: colors.surface,
  },
  questionSection: {
    marginBottom: spacing.sectionGap,
  },
  questionLabel: {
    fontSize: '13px',
    fontWeight: fontWeights.semibold,
    color: colors.cardBodyText,
    marginBottom: spacing.elementGap,
  },
  optionGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.elementGap,
  },
  optionButton: (isSelected) => ({
    ...pillButtonStyle,
    padding: '8px 16px',
    backgroundColor: isSelected ? colors.cardHeaderBackground : colors.surface,
    color: isSelected ? colors.cardHeaderText : colors.cardBodyText,
    border: isSelected ? `1.5px solid ${colors.cardHeaderBackground}` : `1.5px solid ${colors.divider}`,
    whiteSpace: 'nowrap',
  }),
  buttonRow: {
    display: 'flex',
    gap: spacing.elementGap,
    flexWrap: 'wrap',
    marginTop: spacing.sectionGap,
  },
  confirmButton: (disabled) => ({
    ...pillButtonStyle,
    padding: '10px 20px',
    backgroundColor: disabled ? colors.divider : colors.cardHeaderBackground,
    color: colors.cardHeaderText,
    fontWeight: fontWeights.semibold,
    fontSize: typography.body.fontSize,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'opacity 0.15s',
  }),
  dismissButton: {
    ...pillButtonStyle,
    padding: '10px 20px',
    backgroundColor: colors.surface,
    color: colors.cardBodyText,
    border: `1.5px solid ${colors.divider}`,
    fontSize: typography.body.fontSize,
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

const QuickSetupPrompt = ({ tripId, userId, flights, onComplete, onDismiss }) => {
  const [selections, setSelections] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Classify flight legs to determine which questions to show
  const classifiedLegs = classifyFlightLegs(flights);

  const departureLeg = classifiedLegs.find((leg) => leg.legType === 'departure');
  const arrivalLeg = classifiedLegs.find((leg) => leg.legType === 'arrival');

  const showTransportMode = !!departureLeg;
  const showCheckedBags = !!arrivalLeg;

  // Check if all required questions are answered
  const allAnswered =
    (!showTransportMode || selections.transportMode !== undefined) &&
    (!showCheckedBags || selections.hasCheckedBags !== undefined);

  // ── Handle selection ────────────────────────────────────────────────────
  const handleSelect = useCallback((key, value) => {
    setSelections((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Handle confirm ──────────────────────────────────────────────────────
  const handleConfirm = useCallback(async () => {
    if (!allAnswered || isSaving) return;

    // Validate each selection before saving
    for (const [key, value] of Object.entries(selections)) {
      const result = validatePreference(key, value);
      if (!result.valid) {
        console.error('Validation failed:', result.error);
        return;
      }
    }

    setIsSaving(true);

    const payload = {
      ...selections,
      source: 'trip_creation',
      updatedAt: Date.now(),
    };

    try {
      await saveJourneyPreferences(userId, tripId, payload);
    } catch (err) {
      console.error('Error saving quick setup preferences:', err);
      // Still call onComplete so the trip creation flow isn't blocked
    } finally {
      setIsSaving(false);
    }

    if (onComplete) {
      onComplete(payload);
    }
  }, [allAnswered, isSaving, selections, userId, tripId, onComplete]);

  // ── Handle dismiss ──────────────────────────────────────────────────────
  const handleDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss();
    }
  }, [onDismiss]);

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={styles.container} data-testid="quick-setup-prompt">
      <div style={styles.header}>
        <Plane size={18} />
        <span style={styles.headerText}>Quick Journey Setup</span>
      </div>
      <div style={styles.body}>
        {/* Transport mode question for departure */}
        {showTransportMode && (
          <div style={styles.questionSection} data-testid="question-transportMode">
            <div style={styles.questionLabel}>How are you getting to the airport?</div>
            <div style={styles.optionGrid}>
              {TRANSPORT_OPTIONS.map((option) => {
                const isSelected = selections.transportMode === option.value;
                const OptionIcon = option.Icon;
                return (
                  <button
                    key={option.value}
                    style={styles.optionButton(isSelected)}
                    onClick={() => handleSelect('transportMode', option.value)}
                    disabled={isSaving}
                    data-testid={`option-transportMode-${option.value}`}
                    aria-pressed={isSelected}
                  >
                    {OptionIcon && <OptionIcon size={14} />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Checked bags question for arrival */}
        {showCheckedBags && (
          <div style={styles.questionSection} data-testid="question-hasCheckedBags">
            <div style={styles.questionLabel}>Do you have checked bags?</div>
            <div style={styles.optionGrid}>
              {CHECKED_BAGS_OPTIONS.map((option) => {
                const isSelected = selections.hasCheckedBags === option.value;
                const OptionIcon = option.Icon;
                return (
                  <button
                    key={String(option.value)}
                    style={styles.optionButton(isSelected)}
                    onClick={() => handleSelect('hasCheckedBags', option.value)}
                    disabled={isSaving}
                    data-testid={`option-hasCheckedBags-${option.value}`}
                    aria-pressed={isSelected}
                  >
                    {OptionIcon && <OptionIcon size={14} />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div style={styles.buttonRow}>
          <button
            style={styles.confirmButton(!allAnswered || isSaving)}
            onClick={handleConfirm}
            disabled={!allAnswered || isSaving}
            data-testid="confirm-setup-btn"
          >
            <Check size={16} />
            {isSaving ? 'Saving...' : 'Confirm'}
          </button>
          <button
            style={styles.dismissButton}
            onClick={handleDismiss}
            disabled={isSaving}
            data-testid="dismiss-setup-btn"
          >
            <X size={16} />
            Skip
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickSetupPrompt;
